import {
  AccountCancellationPolicyDto,
  DEFAULT_CANCELLATION_POLICY_TEXT,
  DEFAULT_CANCELLATION_POLICY_WINDOW_UNIT,
  DEFAULT_CANCELLATION_POLICY_WINDOW_VALUE,
} from '@shared/core/cancellation-policy'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import type {
  CancellationPolicyFormErrors,
  CancellationPolicyFormValues,
} from '../../types/cancellation-policy-form.types'
import { usePolicyFieldHandlers } from '../../hooks/use-policy-field-handlers'
import { toPolicyFormValues } from '../../utils'

const OFF_DEFAULTS: CancellationPolicyFormValues = {
  enabled: false,
  notice_value: String(DEFAULT_CANCELLATION_POLICY_WINDOW_VALUE),
  notice_unit: DEFAULT_CANCELLATION_POLICY_WINDOW_UNIT,
  allow_late_cancel: true,
  allow_reschedule_in_window: true,
  policy_text: DEFAULT_CANCELLATION_POLICY_TEXT,
  policy_text_edited: false,
}

const DIRTY = { shouldDirty: true }

/** Form state, pre-fill and the turn-on/turn-off rules of the account policy section. */
export function usePolicyForm(saved: AccountCancellationPolicyDto | undefined) {
  const form = useForm<CancellationPolicyFormValues>({
    defaultValues: saved ? toPolicyFormValues(saved) : OFF_DEFAULTS,
  })
  const { reset, setValue, formState } = form
  const [errors, setErrors] = useState<CancellationPolicyFormErrors>({})
  const turnOnConfirmedRef = useRef(false)
  const isDirty = formState.isDirty

  useEffect(() => {
    if (saved && !isDirty) reset(toPolicyFormValues(saved))
    // Only a new server row may reset the form; unsaved edits must survive other refetches.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saved, reset])

  const { onNoticeChange, onTextChange } = usePolicyFieldHandlers(form, setErrors)

  const confirmTurnOn = useCallback(() => {
    turnOnConfirmedRef.current = true
    setValue('enabled', true, DIRTY)
  }, [setValue])

  const turnOff = useCallback(() => {
    const restored = saved ? toPolicyFormValues(saved) : OFF_DEFAULTS
    for (const [field, value] of Object.entries(restored)) {
      setValue(field as keyof CancellationPolicyFormValues, value, DIRTY)
    }
    setValue('enabled', false, DIRTY)
    setErrors({})
  }, [saved, setValue])

  return { form, errors, setErrors, turnOnConfirmedRef, onNoticeChange, onTextChange, confirmTurnOn, turnOff }
}
